import {
  BindEdge,
  Connection,
  ContractDirection,
  ContractStatus,
  ScopeAccount,
} from '../../../../types/responses/Interoperability';
import { DIRECTION_ARROW } from '../contractRules';


/**
 * Geometria do mapa de ligações (MV2-1719).
 *
 * Fica fora do componente porque é cálculo, não marcação: posição de nó, curva
 * de ligação e o que fica apagado quando um parceiro está selecionado. Assim o
 * desenho pode ser conferido por teste, sem montar SVG.
 */

/** Parceiro no mapa — de um contrato existente ou de um convite pendente. */
export type MapPartner = {
  tenantId: string;
  name: string;
  contractType: ContractDirection;
  status: ContractStatus;
};

export type MapNodeKind = 'root' | 'account' | 'partner';

export type MapNode = {
  id: string;
  kind: MapNodeKind;
  x: number;
  y: number;
  width: number;
  height: number;
  title: string;

  /** Contagem de estações, ou o tipo de contrato. */
  subtitle: string;

  /** "matriz", "conta", ou o status do contrato. */
  tag: string;

  dimmed: boolean;
  clickable: boolean;

  /**
   * Se a pessoa pode mover o no.
   *
   * Subconta e falso: a posicao dela abaixo da matriz nao e escolha de desenho, e a
   * hierarquia. Arrastar a matriz leva as subcontas junto — elas sao fixas em
   * relacao a ela, nao a tela.
   */
  draggable: boolean;
};

/** Caixa de um no. Serve para o arraste sobrescrever a posicao calculada. */
export type MapBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Contrato e uma ligacao negociada; dependencia e a hierarquia da propria conta.
 * Sao coisas diferentes e nao podem ter o mesmo traco.
 */
export type MapLinkKind = 'contract' | 'dependency';

export type MapLink = {
  id: string;
  kind: MapLinkKind;
  d: string;
  color: string;
  dashed: boolean;

  /** Ponta da seta, por cor: SVG exige um marker por cor de traco. */
  markerId: string;

  /** 'out' = eu compartilho; 'in' = eu recebo. */
  direction: 'out' | 'in';

  dimmed: boolean;
  partnerTenantId: string;
};

export type MapLayout = {
  width: number;
  height: number;
  nodes: MapNode[];
  links: MapLink[];
};

const NODE_H = 46;
const NODE_GAP = 12;
const COL_W = 190;
const TOP = 46;

const DIM_STROKE = '#e6e9ed';

/**
 * Um marker por cor, porque o SVG nao herda `stroke` no marcador: a ponta da seta
 * sai preta se nao tiver preenchimento proprio. Os ids batem com as cores do
 * linkStyleFor.
 */
export const LINK_MARKERS: { id: string; color: string }[] = [
  { id: 'interopArrowBidirectional', color: '#2f6fe0' },
  { id: 'interopArrowOneWay', color: '#9aa3af' },
  { id: 'interopArrowPending', color: '#e0b25e' },
  { id: 'interopArrowSuspended', color: '#e0700f' },
  { id: 'interopArrowDim', color: DIM_STROKE },
];

const MARKER_BY_COLOR: Record<string, string> = LINK_MARKERS.reduce(
  (index, marker) => ({ ...index, [marker.color]: marker.id }),
  {},
);

/**
 * Cor e traço da ligação, pelo estado do contrato.
 *
 * Pontilhado significa "não está compartilhando agora": convite que ninguém
 * aceitou e contrato suspenso caem no mesmo traço porque, para quem olha o mapa,
 * o efeito prático é o mesmo — nenhuma estação aparece.
 */
export const linkStyleFor = (
  partner: MapPartner,
): { color: string; dashed: boolean } => {
  if (partner.status === 'PENDING') {
    return { color: '#e0b25e', dashed: true };
  }

  if (partner.status === 'SUSPENDED') {
    return { color: '#e0700f', dashed: true };
  }

  return partner.contractType === 'BIDIRECTIONAL'
    ? { color: '#2f6fe0', dashed: false }
    : { color: '#9aa3af', dashed: false };
};

/** Parceiros do mapa: contratos existentes mais convites ainda pendentes. */
export const partnersOf = (
  connections: Connection[],
  pendingPartners: { tenantId: string; name: string; contractType: ContractDirection }[],
): MapPartner[] => {
  const fromContracts: MapPartner[] = (connections || [])
    .filter((connection) => connection.status !== 'REVOKED')
    .map((connection) => ({
      tenantId: connection.partner_tenant_id,
      name: connection.partner_name,
      contractType: connection.contract_type,
      status: connection.status,
    }));

  const known = new Set(fromContracts.map((partner) => partner.tenantId));

  // Um convite pendente para quem já tem contrato não vira segundo nó: seria a
  // mesma organização desenhada duas vezes.
  const fromInvites: MapPartner[] = (pendingPartners || [])
    .filter((partner) => !known.has(partner.tenantId))
    .map((partner) => ({ ...partner, status: 'PENDING' as ContractStatus }));

  return [...fromContracts, ...fromInvites];
};

/**
 * Curva de uma ponta a outra, no sentido em que e percorrida.
 *
 * O tirante acompanha o sentido: numa curva da direita para a esquerda, empurrar os
 * pontos de controle sempre para a direita faria um S grande e feio em vez do
 * espelho da curva de ida.
 */
const curve = (x1: number, y1: number, x2: number, y2: number): string => {
  const bend = Math.max(60, Math.abs(x2 - x1) / 2);
  const towards = x2 >= x1 ? 1 : -1;

  return `M${x1},${y1} C${x1 + bend * towards},${y1} ${x2 - bend * towards},${y2} ${x2},${y2}`;
};

type LayoutInput = {
  accounts: ScopeAccount[];
  partners: MapPartner[];
  edges: BindEdge[];
  selectedPartnerId?: string | null;
  accountLabel: (count: number) => string;
  rootTag: string;
  accountTag: string;

  /**
   * Area disponivel na tela. O desenho ocupa o espaco em vez de um tamanho fixo:
   * o mapa e a unica aba sem tabela, e sobrava area vazia embaixo dele.
   */
  width?: number;
  height?: number;
};

/**
 * Ligações que existem entre uma conta minha e um parceiro, pelas arestas.
 *
 * Convite pendente não tem aresta nenhuma — nada foi compartilhado ainda —, e
 * por isso ele é ligado à conta raiz: o mapa mostra a intenção, pontilhada.
 */
export const buildLinks = (
  input: LayoutInput,
  positionOf: (id: string) => MapBox | undefined,
  rootAccountId: string | undefined,
): MapLink[] => {
  const accountIds = new Set(input.accounts.map((account) => account.tenant_id));
  const partnerById = new Map(input.partners.map((partner) => [partner.tenantId, partner]));
  const links: MapLink[] = [];

  const push = (accountId: string, partnerTenantId: string, direction: 'out' | 'in', key: string) => {
    const partner = partnerById.get(partnerTenantId);
    const account = positionOf(accountId);
    const partnerBox = positionOf(partnerTenantId);

    if (!partner || !account || !partnerBox) {
      return;
    }

    const dimmed = !!input.selectedPartnerId && input.selectedPartnerId !== partnerTenantId;
    const style = linkStyleFor(partner);

    // A volta sai um pouco abaixo da ida para as duas curvas não se sobreporem
    const offset = direction === 'in' ? 9 : 0;

    const atAccount = {
      x: account.x + account.width,
      y: account.y + account.height / 2 + offset,
    };
    const atPartner = {
      x: partnerBox.x,
      y: partnerBox.y + partnerBox.height / 2 + offset,
    };

    // A seta fica na ponta que RECEBE. Na ida, o parceiro passa a exibir minhas
    // estacoes: a seta aponta para ele. Na volta sou eu que exibo as dele, e a seta
    // tem de chegar na minha conta — por isso o caminho e percorrido ao contrario, em
    // vez de repetir a mesma direcao com dois tracos iguais.
    const from = direction === 'in' ? atPartner : atAccount;
    const to = direction === 'in' ? atAccount : atPartner;

    links.push({
      id: key,
      kind: 'contract',
      markerId: dimmed ? 'interopArrowDim' : MARKER_BY_COLOR[style.color] || 'interopArrowOneWay',
      d: curve(from.x, from.y, to.x, to.y),
      color: dimmed ? DIM_STROKE : style.color,
      dashed: style.dashed || direction === 'in',
      direction,
      dimmed,
      partnerTenantId,
    });
  };

  input.edges.forEach((edge) => {
    if (accountIds.has(edge.tenant_target_id) && partnerById.has(edge.tenant_source_id)) {
      push(edge.tenant_target_id, edge.tenant_source_id, 'out',
        `out-${edge.tenant_target_id}-${edge.tenant_source_id}`);
      return;
    }

    if (accountIds.has(edge.tenant_source_id) && partnerById.has(edge.tenant_target_id)) {
      push(edge.tenant_source_id, edge.tenant_target_id, 'in',
        `in-${edge.tenant_source_id}-${edge.tenant_target_id}`);
    }
  });

  const linkedPartners = new Set(links.map((link) => link.partnerTenantId));

  if (rootAccountId) {
    input.partners
      .filter((partner) => !linkedPartners.has(partner.tenantId))
      .forEach((partner) =>
        push(rootAccountId, partner.tenantId, 'out', `pending-${partner.tenantId}`));
  }

  return links;
};

export const rootIdOf = (accounts: ScopeAccount[]): string | undefined =>
  (accounts.find((account) => account.root) || accounts[0])?.tenant_id;

const DEPENDENCY_STROKE = '#c6cbd3';

/**
 * Ligacoes de dependencia: da conta raiz para cada subconta dela.
 *
 * Traco proprio, sem seta e sem cor de contrato: nao ha nada negociado aqui, e a
 * subconta nao "compartilha" com a matriz — ela pertence a ela. Usar o mesmo
 * desenho das ligacoes de contrato faria a hierarquia parecer um acordo.
 *
 * O caminho e em cotovelo, e nao curva: dependencia se le melhor como estrutura, e
 * a curva ficaria confundida com as ligacoes de contrato ao lado.
 */
export const dependencyLinks = (
  accounts: ScopeAccount[],
  positionOf: (id: string) => MapBox | undefined,
): MapLink[] => {
  const rootId = rootIdOf(accounts);
  const root = rootId ? positionOf(rootId) : undefined;

  if (!rootId || !root) {
    return [];
  }

  return accounts
    .filter((account) => account.tenant_id !== rootId)
    .map((account) => {
      const box = positionOf(account.tenant_id);

      if (!box) {
        return null;
      }

      const fromX = root.x + 22;
      const fromY = root.y + root.height;
      const toY = box.y + box.height / 2;

      return {
        id: `dep-${account.tenant_id}`,
        kind: 'dependency' as const,
        d: `M${fromX},${fromY} L${fromX},${toY} L${box.x},${toY}`,
        color: DEPENDENCY_STROKE,
        dashed: false,
        markerId: '',
        direction: 'out' as const,
        dimmed: false,
        partnerTenantId: '',
      };
    })
    .filter((link) => link !== null) as MapLink[];
};

/**
 * Visão Fluxo: minhas contas à esquerda, parceiros à direita.
 *
 * A altura acompanha a coluna mais alta — com muitas subcontas ou muitos
 * parceiros o desenho cresce em vez de comprimir os nós até ficarem ilegíveis.
 */
export const buildFlowLayout = (input: LayoutInput): MapLayout => {
  // Dois pixels a menos que a area medida: o getBoundingClientRect inclui a borda,
  // e o desenho exato do tamanho do container faz nascer barra horizontal.
  const width = Math.max(input.width ? input.width - 2 : 820, 620);
  const leftX = 16;
  const rightX = width - COL_W - 16;

  const nodes: MapNode[] = [];
  const boxes = new Map<string, { x: number; y: number; width: number; height: number }>();

  // A raiz primeiro; as subcontas recuadas logo abaixo dela, para a coluna ler como
  // hierarquia e nao como lista de iguais.
  const rootId = rootIdOf(input.accounts);
  const ordered = [
    ...input.accounts.filter((account) => account.tenant_id === rootId),
    ...input.accounts.filter((account) => account.tenant_id !== rootId),
  ];

  ordered.forEach((account, index) => {
    const isRoot = account.tenant_id === rootId;
    const y = TOP + index * (NODE_H + NODE_GAP);
    const box = {
      x: isRoot ? leftX : leftX + 22,
      y,
      width: isRoot ? COL_W : COL_W - 22,
      height: NODE_H,
    };

    boxes.set(account.tenant_id, box);
    nodes.push({
      id: account.tenant_id,
      kind: isRoot ? 'root' : 'account',
      ...box,
      title: account.name,
      subtitle: input.accountLabel(account.station_count),
      tag: isRoot ? input.rootTag : input.accountTag,
      dimmed: false,
      clickable: false,
      // Subconta nao se move: a posicao dela e a hierarquia.
      draggable: isRoot,
    });
  });

  input.partners.forEach((partner, index) => {
    const y = TOP + index * (NODE_H + NODE_GAP);
    const box = { x: rightX, y, width: COL_W, height: NODE_H };

    boxes.set(partner.tenantId, box);
    nodes.push({
      id: partner.tenantId,
      kind: 'partner',
      ...box,
      title: partner.name,
      subtitle: DIRECTION_ARROW[partner.contractType],
      tag: partner.status,
      dimmed: !!input.selectedPartnerId && input.selectedPartnerId !== partner.tenantId,
      clickable: true,
      draggable: true,
    });
  });

  const rows = Math.max(input.accounts.length, input.partners.length, 1);

  // Ocupa a area disponivel, e cresce alem dela quando ha mais linhas do que cabe:
  // comprimir os nos ate ficarem ilegiveis seria pior que rolar.
  const needed = TOP + rows * (NODE_H + NODE_GAP) + 8;

  return {
    width,
    height: Math.max(needed, input.height || 0),
    nodes,
    links: [
      ...dependencyLinks(input.accounts, (id) => boxes.get(id)),
      ...buildLinks(input, (id) => boxes.get(id), rootIdOf(input.accounts)),
    ],
  };
};

/**
 * Visão Mapa mental: raiz no centro, subcontas à esquerda, parceiros em volta.
 *
 * Os ângulos são fixos e distribuídos no arco disponível — não há simulação de
 * força, que daria um desenho diferente a cada abertura da tela.
 */
export const buildMentalLayout = (input: LayoutInput): MapLayout => {
  const width = Math.max(input.width ? input.width - 2 : 860, 620);
  const height = Math.max(input.height ? input.height - 2 : 460, 420);
  const cx = width / 2;
  const cy = height / 2;

  // O raio acompanha a area, com folga para a caixa do no nao sair pela borda.
  const radius = Math.max(180, Math.min(width / 2 - 110, height / 2 - 60));

  const nodes: MapNode[] = [];
  const boxes = new Map<string, { x: number; y: number; width: number; height: number }>();

  const rootId = rootIdOf(input.accounts);

  const place = (
    id: string,
    kind: MapNodeKind,
    title: string,
    subtitle: string,
    tag: string,
    x: number,
    y: number,
    w: number,
    h: number,
    dimmed: boolean,
    clickable: boolean,
    draggable: boolean,
  ) => {
    const box = { x, y, width: w, height: h };

    boxes.set(id, box);
    nodes.push({ id, kind, ...box, title, subtitle, tag, dimmed, clickable, draggable });
  };

  const branches = input.accounts.filter((account) => account.tenant_id !== rootId);
  const spread = (index: number, total: number, from: number, to: number): number =>
    total <= 1 ? (from + to) / 2 : from + ((to - from) * index) / (total - 1);

  input.accounts
    .filter((account) => account.tenant_id === rootId)
    .forEach((account) =>
      place(account.tenant_id, 'root', account.name,
        input.accountLabel(account.station_count), input.rootTag,
        cx - 90, cy - 26, 180, 52, false, false, true));

  // Subcontas em leque ABAIXO da raiz, e nao a esquerda: elas dependem dela, e
  // abaixo e onde a dependencia se le sem legenda. Ficam fixas — a posicao e a
  // hierarquia, nao escolha de desenho.
  branches.forEach((account, index) => {
    const angle = (spread(index, branches.length, 62, 118) * Math.PI) / 180;
    const branchRadius = Math.max(140, radius * 0.62);

    place(account.tenant_id, 'account', account.name,
      input.accountLabel(account.station_count), input.accountTag,
      cx + branchRadius * Math.cos(angle) - 76, cy + branchRadius * Math.sin(angle) - 20,
      152, 40, false, false, false);
  });

  input.partners.forEach((partner, index) => {
    const angle = (spread(index, input.partners.length, -62, 62) * Math.PI) / 180;

    place(partner.tenantId, 'partner', partner.name,
      DIRECTION_ARROW[partner.contractType], partner.status,
      cx + radius * Math.cos(angle) - 84, cy + radius * Math.sin(angle) - 22,
      168, 44,
      !!input.selectedPartnerId && input.selectedPartnerId !== partner.tenantId, true, true);
  });

  return {
    width,
    height,
    nodes,
    links: [
      ...dependencyLinks(input.accounts, (id) => boxes.get(id)),
      ...buildLinks(input, (id) => boxes.get(id), rootId),
    ],
  };
};
